// Super admin hands a schedule to another owner — it then runs in that person's scope.
import { useEffect, useState } from "react";
import { Loader2, Search, UserCog } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export function ReassignOwnerDialog({ schedule, open, onClose }) {
  const [q, setQ] = useState("");
  const [users, setUsers] = useState(null);
  const [picked, setPicked] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => {
      api.get("/report-hub/owner-candidates", { params: { q } }).then((r) => setUsers(r.data.users || [])).catch(() => setUsers([]));
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  const apply = async () => {
    if (!picked) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/report-hub/schedules/${schedule.schedule_id}/reassign`, { owner_user_id: picked.user_id });
      toast.success(`"${schedule.name}" now belongs to ${data.owner_name} and runs in their scope`);
      onClose(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not hand off the schedule"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose(null)}>
      <DialogContent className="max-w-lg" data-testid="reassign-dialog">
        <DialogHeader>
          <DialogTitle>Hand off “{schedule.name}”</DialogTitle>
          <DialogDescription>
            Currently owned by {schedule.owner_name}. The new owner must be allowed to run {schedule.report_label};
            the schedule will then only include data that person can see.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
            <Input className="h-9 pl-8" placeholder="Search people by name or email…" value={q} onChange={(e) => setQ(e.target.value)}
                   data-testid="reassign-search" autoFocus />
          </div>
          <div className="max-h-64 divide-y divide-slate-100 overflow-y-auto rounded-lg border border-slate-200" data-testid="reassign-candidates">
            {users === null ? (
              <div className="py-6 text-center text-slate-400"><Loader2 className="mx-auto h-4 w-4 animate-spin" /></div>
            ) : users.length === 0 ? (
              <div className="py-6 text-center text-sm text-slate-400">No active users match.</div>
            ) : users.filter((u) => u.user_id !== schedule.owner_user_id).map((u) => (
              <button type="button" key={u.user_id} onClick={() => setPicked(u)} data-testid={`reassign-candidate-${u.user_id}`}
                      className={`flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-sm transition-colors ${
                        picked?.user_id === u.user_id ? "bg-[#eef2fb] text-primary" : "hover:bg-slate-50"}`}>
                <span className="min-w-0">
                  <span className="block truncate font-medium">{u.name}</span>
                  <span className="block truncate text-xs text-slate-500">{u.email}</span>
                </span>
                <span className="shrink-0 text-right text-[11px] text-slate-500">{u.role}{u.organization ? ` · ${u.organization}` : ""}</span>
              </button>
            ))}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onClose(null)} data-testid="reassign-cancel">Cancel</Button>
          <Button onClick={apply} disabled={!picked || busy} data-testid="reassign-apply">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserCog className="h-4 w-4" />} Hand off{picked ? ` to ${picked.name}` : ""}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
